var _dec, _class, _Slider;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { addEventListener } from '@instructure/ui-dom-utils';
import { withStyle } from '@instructure/emotion';
import { View } from '@instructure/ui-view';
import { propTypes, allowedProps } from './props';
import generateStyle from './styles';
import generateComponentTheme from './theme';

/**
---
private: true
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
let Slider = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_Slider = class Slider extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this._sliderRef = null;
    this._mouseMoveListener = void 0;
    this._mouseUpListener = void 0;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.handleChange = e => {
      if (this.props.disabled) return;
      const clientX = e.clientX;
      const newPosition = this.calcSliderPositionFromCursorPosition(clientX, this._sliderRef);
      this.props.onChange(this.calcValueFromSliderPosition(newPosition));
    };
    this.handleMouseUp = () => {
      this.removeEventListeners();
    };
    this.calcSliderPositionFromCursorPosition = (clientX, _sliderRef) => {
      if (this.props.isColorSlider) {
        const _sliderRef$getBoundin = _sliderRef.getBoundingClientRect(),
          x = _sliderRef$getBoundin.x;
        const newPosition = clientX - x;
        return newPosition < 0 ? 0 : newPosition > this.props.width ? this.props.width - 1 : newPosition;
      } else {
        const _sliderRef$getBoundin2 = _sliderRef.getBoundingClientRect(),
          x = _sliderRef$getBoundin2.x;
        return clientX - x;
      }
    };
    this.calcSliderPositionFromValue = value => {
      if (this.props.isColorSlider) {
        return value / 360 * this.props.width;
      } else {
        return this.props.width - (1 - value) * this.props.width;
      }
    };
    this.calcValueFromSliderPosition = position => {
      if (this.props.isColorSlider) {
        return position / this.props.width * 360;
      } else {
        const positionWithBoundaries = position < 0 ? 0 : position > this.props.width ? this.props.width : position;
        return Math.round(positionWithBoundaries * 100 / this.props.width);
      }
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.makeStylesProps);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, this.makeStylesProps);
  }
  componentWillUnmount() {
    this.removeEventListeners();
  }
  removeEventListeners() {
    var _this$_mouseMoveListe, _this$_mouseUpListene;
    (_this$_mouseMoveListe = this._mouseMoveListener) === null || _this$_mouseMoveListe === void 0 ? void 0 : _this$_mouseMoveListe.remove();
    (_this$_mouseUpListene = this._mouseUpListener) === null || _this$_mouseUpListene === void 0 ? void 0 : _this$_mouseUpListene.remove();
  }
  get sliderPositionFromValue() {
    return this.calcSliderPositionFromValue(this.props.value);
  }
  get roundedValue() {
    const _this$props3 = this.props,
      value = _this$props3.value,
      maxValue = _this$props3.maxValue;
    if (maxValue <= 1) {
      return Math.round(value * 100);
    } else {
      return Math.round(value);
    }
  }
  get makeStylesProps() {
    return {
      sliderPositionFromValue: this.sliderPositionFromValue
    };
  }
  handleMouseDown(e) {
    this.handleChange(e);
    this._mouseMoveListener = addEventListener(window, 'mousemove', this.handleChange);
    this._mouseUpListener = addEventListener(window, 'mouseup', this.handleMouseUp);
  }
  applyBoundaries(x) {
    if (x > this.props.width) return this.props.width;
    if (x < 0) return 0;
    return x;
  }
  handleKeyDown(e) {
    const key = e.key;
    if (key === 'Tab') return;
    e.preventDefault();
    let deltaX = 0;
    if (key === 'ArrowLeft' || key === 'a') {
      deltaX = -2;
    }
    if (key === 'ArrowRight' || key === 'd') {
      deltaX = 2;
    }
    const newPosition = this.applyBoundaries(this.sliderPositionFromValue + deltaX);
    this.props.onChange(this.calcValueFromSliderPosition(newPosition));
  }
  render() {
    var _this$props$styles, _this$props$styles2, _this$props$styles3, _this$props$styles4;
    return _jsxs(View, {
      elementRef: this.handleRef,
      disabled: this.props.disabled,
      position: "relative",
      background: "transparent",
      margin: "small 0 0 0",
      display: "inline-block",
      borderRadius: "medium",
      borderWidth: "0",
      padding: "0",
      as: "div",
      onKeyDown: e => this.handleKeyDown(e),
      onMouseDown: e => this.handleMouseDown(e),
      tabIndex: this.props.disabled ? void 0 : 0,
      "aria-label": this.props.navigationExplanationScreenReaderLabel,
      role: "slider",
      "aria-valuemin": this.props.minValue,
      "aria-valuemax": this.props.maxValue,
      "aria-valuenow": this.roundedValue,
      children: [_jsx("div", {
        css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.indicator
      }), this.props.disabled && _jsx("div", {
        css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.disabledOverlay
      }), _jsx("div", {
        ref: ref => {
          this._sliderRef = ref;
        },
        css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.sliderBackground,
        children: _jsx("div", {
          css: (_this$props$styles4 = this.props.styles) === null || _this$props$styles4 === void 0 ? void 0 : _this$props$styles4.slider
        })
      })]
    });
  }
}, _Slider.displayName = "Slider", _Slider.propTypes = propTypes, _Slider.allowedProps = allowedProps, _Slider.componentId = 'ColorMixer.Slider', _Slider.defaultProps = {
  isColorSlider: false
}, _Slider)) || _class);
export default Slider;